/**
 * CampaignWizardPage — campaign creation at /campaigns/new.
 *
 * Single-page form (objective → target → budget → autonomy). The promoted
 * target is either a Fias arche id (Fias's own growth use case: the
 * landing URL is derived server-side from the arche row) or an explicit
 * https landing URL for contributor campaigns.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateAdoptCampaign } from '../hooks/useAdvertisingOptimization';
import { ARCHE_PATH } from '../constants';
import type { AdoptObjectiveDto, AdoptAutonomyLevelDto } from '@/lib/api';

interface Props {
  holderId: string;
}

const OBJECTIVES: Array<{ value: AdoptObjectiveDto; label: string; hint: string }> = [
  { value: 'cpa', label: 'Cost per acquisition', hint: 'Minimize cost per conversion' },
  { value: 'roas', label: 'Return on ad spend', hint: 'Maximize revenue ÷ spend' },
  { value: 'ltv_90d', label: '90-day LTV', hint: 'Maximize 90-day customer value' },
  { value: 'conversions', label: 'Conversions', hint: 'Maximize conversion volume' },
  { value: 'clicks', label: 'Clicks', hint: 'Maximize qualified traffic' },
  { value: 'awareness', label: 'Awareness', hint: 'Maximize reach/impressions' },
];

const AUTONOMY: Array<{ value: AdoptAutonomyLevelDto; label: string; hint: string }> = [
  { value: 'manual', label: 'Manual', hint: 'Engine only recommends; you act' },
  {
    value: 'propose',
    label: 'Propose & approve',
    hint: 'Engine proposes; you approve each action',
  },
  { value: 'auto', label: 'Automatic', hint: 'Engine acts within your guardrails' },
];

export function CampaignWizardPage({ holderId }: Props) {
  const navigate = useNavigate();
  const createMutation = useCreateAdoptCampaign(holderId);

  const [name, setName] = useState('');
  const [targetMode, setTargetMode] = useState<'arche' | 'url'>('arche');
  const [promotedArcheId, setPromotedArcheId] = useState('');
  const [landingUrl, setLandingUrl] = useState('');
  const [landingLabel, setLandingLabel] = useState('');
  const [objective, setObjective] = useState<AdoptObjectiveDto>('cpa');
  const [totalBudget, setTotalBudget] = useState('500');
  const [dailyCeiling, setDailyCeiling] = useState('25');
  const [autonomy, setAutonomy] = useState<AdoptAutonomyLevelDto>('propose');
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setError(null);
    const totalCents = Math.round(parseFloat(totalBudget) * 100);
    const dailyCents = Math.round(parseFloat(dailyCeiling) * 100);
    if (!name.trim()) return setError('Name is required');
    if (!Number.isFinite(totalCents) || totalCents <= 0)
      return setError('Total budget must be positive');
    if (!Number.isFinite(dailyCents) || dailyCents <= 0)
      return setError('Daily ceiling must be positive');
    // Both shapes the platform mints: hand-named platform arches
    // (arche_…) and contributor plugins (arc_ + 32 hex). Mirrors
    // PROMOTABLE_ARCHE_ID on the server.
    if (targetMode === 'arche' && !/^arc(he)?_[a-z0-9_]+$/.test(promotedArcheId.trim())) {
      return setError('Enter a valid arche id (arche_… or arc_…)');
    }
    if (targetMode === 'url' && !landingUrl.startsWith('https://')) {
      return setError('Landing URL must be https://');
    }

    try {
      const result = await createMutation.mutateAsync({
        name: name.trim(),
        ...(targetMode === 'arche'
          ? { promotedArcheId: promotedArcheId.trim() }
          : {
              promotedTarget: {
                kind: 'url' as const,
                label: landingLabel.trim() || landingUrl,
                landingUrl,
              },
            }),
        objective,
        totalBudgetCents: totalCents,
        dailyBudgetCeilingCents: dailyCents,
        startsAt: new Date().toISOString(),
        autonomyLevel: autonomy,
      });
      navigate(`${ARCHE_PATH}/campaigns/${result.campaign.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold">New campaign</h1>
        <p className="text-sm text-muted-foreground">
          Define what to promote, what success means, and how much control the engine gets.
        </p>
      </header>

      <section className="flex flex-col gap-2">
        <label className="text-sm font-medium" htmlFor="adopt-name">
          Campaign name
        </label>
        <input
          id="adopt-name"
          className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder='e.g. "Free Photoshop — search"'
          maxLength={255}
        />
      </section>

      <section className="flex flex-col gap-2">
        <span className="text-sm font-medium">What are you promoting?</span>
        <div className="flex gap-2">
          <button
            onClick={() => setTargetMode('arche')}
            className={`px-3 py-1.5 rounded-md text-sm border ${targetMode === 'arche' ? 'border-primary font-medium' : 'border-border text-muted-foreground'}`}
          >
            A Fias arche
          </button>
          <button
            onClick={() => setTargetMode('url')}
            className={`px-3 py-1.5 rounded-md text-sm border ${targetMode === 'url' ? 'border-primary font-medium' : 'border-border text-muted-foreground'}`}
          >
            A landing URL
          </button>
        </div>
        {targetMode === 'arche' ? (
          <input
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={promotedArcheId}
            onChange={(e) => setPromotedArcheId(e.target.value)}
            placeholder="arche_image_editor"
          />
        ) : (
          <div className="flex flex-col gap-2">
            <input
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={landingUrl}
              onChange={(e) => setLandingUrl(e.target.value)}
              placeholder="https://…"
            />
            <input
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={landingLabel}
              onChange={(e) => setLandingLabel(e.target.value)}
              placeholder="Label (optional)"
              maxLength={255}
            />
          </div>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <span className="text-sm font-medium">Optimization objective</span>
        <div className="grid grid-cols-2 gap-2">
          {OBJECTIVES.map((o) => (
            <button
              key={o.value}
              onClick={() => setObjective(o.value)}
              className={`rounded-md border p-3 text-left ${objective === o.value ? 'border-primary' : 'border-border'}`}
            >
              <div className="text-sm font-medium">{o.label}</div>
              <div className="text-xs text-muted-foreground">{o.hint}</div>
            </button>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium" htmlFor="adopt-total">
            Total budget (USD)
          </label>
          <input
            id="adopt-total"
            type="number"
            min="1"
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={totalBudget}
            onChange={(e) => setTotalBudget(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium" htmlFor="adopt-daily">
            Daily ceiling (USD)
          </label>
          <input
            id="adopt-daily"
            type="number"
            min="1"
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={dailyCeiling}
            onChange={(e) => setDailyCeiling(e.target.value)}
          />
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <span className="text-sm font-medium">Autonomy</span>
        <div className="grid grid-cols-3 gap-2">
          {AUTONOMY.map((a) => (
            <button
              key={a.value}
              onClick={() => setAutonomy(a.value)}
              className={`rounded-md border p-3 text-left ${autonomy === a.value ? 'border-primary' : 'border-border'}`}
            >
              <div className="text-sm font-medium">{a.label}</div>
              <div className="text-xs text-muted-foreground">{a.hint}</div>
            </button>
          ))}
        </div>
      </section>

      {error && <div className="text-sm text-rose-600">{error}</div>}

      <div className="flex gap-3">
        <button
          onClick={() => void submit()}
          disabled={createMutation.isPending}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50"
        >
          {createMutation.isPending ? 'Creating…' : 'Create campaign'}
        </button>
        <button
          onClick={() => navigate(`${ARCHE_PATH}/campaigns`)}
          className="px-4 py-2 rounded-md border border-border text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
